import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';

// Выпадающий список с поиском: начинаешь печатать — список сужается.
// Нужен там, где вариантов сотни (судьи, стадионы) и обычный <select>
// приходится долго листать.
const MAX_SHOWN = 60;
const LIST_W = 240;
const LIST_MAX_H = 260;

const normalize = (value) => String(value || '').toLowerCase().replace(/ё/g, 'е').trim();

export default function SearchSelect({
    value, options, onChange, placeholder, emptyLabel, className,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const [pos, setPos] = useState(null);
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const selected = options.find(o => String(o.value) === String(value));

    const filtered = useMemo(() => {
        const q = normalize(query);
        const list = q
            ? options.filter(o => normalize(o.label).includes(q))
            : options;
        return list.slice(0, MAX_SHOWN);
    }, [options, query]);

    // Пункт "не выбрано" всегда первым, если он задан
    const items = emptyLabel ? [{ value: '', label: emptyLabel, empty: true }, ...filtered] : filtered;

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    // Список рисуется position: fixed — таблица матчей лежит в .table-responsive
    // с overflow, и обычный absolute обрезался бы в нижних строках.
    // Если снизу не хватает места — открываем вверх, у правого края — выравниваем вправо.
    useLayoutEffect(() => {
        if (!open) return undefined;
        const place = () => {
            if (!inputRef.current) return;
            const r = inputRef.current.getBoundingClientRect();
            const below = window.innerHeight - r.bottom;
            const up = below < LIST_MAX_H + 12 && r.top > below;
            const left = Math.min(r.left, window.innerWidth - LIST_W - 8);
            setPos({
                left: Math.max(8, left),
                top: up ? undefined : r.bottom + 4,
                bottom: up ? window.innerHeight - r.top + 4 : undefined,
            });
        };
        place();
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);
        return () => {
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [open]);

    useEffect(() => { setActive(emptyLabel && query ? 1 : 0); }, [query, emptyLabel]);

    useEffect(() => {
        if (!open || !listRef.current) return;
        const el = listRef.current.children[active];
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [active, open]);

    const close = () => {
        setOpen(false);
        setQuery('');
        setPos(null);
    };

    const pick = (item) => {
        onChange(item.value === '' ? '' : String(item.value));
        close();
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive(i => Math.min(i + 1, items.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (open && items[active]) pick(items[active]);
        } else if (e.key === 'Escape') {
            close();
        } else if (e.key === 'Tab') {
            close();
        }
    };

    return (
        <div className={`searchSelect ${className || ''}`} ref={wrapRef}>
            <input
                ref={inputRef}
                type="text"
                className="searchSelectInput"
                value={open ? query : (selected ? selected.label : '')}
                placeholder={selected ? selected.label : (placeholder || emptyLabel || '')}
                onFocus={() => setOpen(true)}
                onClick={() => setOpen(true)}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                onKeyDown={onKeyDown}
            />
            {open && pos && (
                <ul
                    className="searchSelectList"
                    ref={listRef}
                    style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: LIST_W, maxHeight: LIST_MAX_H }}
                >
                    {items.length === 0 || (emptyLabel && items.length === 1 && query) ? (
                        <>
                            {emptyLabel && (
                                <li
                                    className={`searchSelectItem searchSelectEmpty ${active === 0 ? 'searchSelectItemActive' : ''}`}
                                    onMouseDown={(e) => { e.preventDefault(); pick(items[0]); }}
                                >
                                    {emptyLabel}
                                </li>
                            )}
                            <li className="searchSelectNone">Ничего не найдено</li>
                        </>
                    ) : items.map((item, index) => (
                        <li
                            key={item.empty ? '__empty' : item.value}
                            className={[
                                'searchSelectItem',
                                item.empty ? 'searchSelectEmpty' : '',
                                index === active ? 'searchSelectItemActive' : '',
                                String(item.value) === String(value) && !item.empty ? 'searchSelectItemSelected' : '',
                            ].join(' ')}
                            onMouseEnter={() => setActive(index)}
                            onMouseDown={(e) => { e.preventDefault(); pick(item); }}
                        >
                            {item.label}
                        </li>
                    ))}
                    {!query && options.length > MAX_SHOWN && (
                        <li className="searchSelectNone">
                            Показаны первые {MAX_SHOWN} из {options.length} — начните вводить для поиска
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
}
